// DEPENDENCIES
import React, { useContext, useEffect, useState } from "react";

// CSS
import "./NewEmployeeDialog.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API CONFIG
import { postAPICall } from "../../../../config/apiCalls";

// MUI COMPONENTS
import {
	Alert,
	Autocomplete,
	Button,
	CircularProgress,
	Collapse,
	Dialog,
	DialogContent,
	DialogTitle,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	TextField,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";

// CONTEXT
import { UserContext } from "../../../../context/UserContext";

// CONSTANTS
import { verifyEmailRegex } from "../../../../constants/regex";
import { formatPhoneNumber } from "../../../../constants/phone";

// REACT COMPONENT
const NewEmployeeDialog = (props) => {
	const employeeID = useContext(UserContext);

	// MSAL
	const { instance, accounts } = useMsal();

	// STATES
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [initials, setInitials] = useState("");

	const [oicID, setOicID] = useState(0);
	const [cellPhone, setCellPhone] = useState("");
	const [deskPhone, setDeskPhone] = useState("");
	const [email, setEmail] = useState("");

	const [hubspotID, setHubspotID] = useState("");
	const [companyCamID, setCompanyCamID] = useState("");
	const [ripplingID, setRipplingID] = useState("");
	const [bqeID, setBqeID] = useState("");
	const [teamsID, setTeamsID] = useState("");
	const [jobTitle, setJobTitle] = useState("");
	const [department, setDepartment] = useState("");
	const [isActive, setIsActive] = useState(1);
	const [employmentType, setEmploymentType] = useState("");
	const [hoursPerWeek, setHoursPerWeek] = useState("");

	const [alertOpen, setAlertOpen] = useState(false);
	const [alertMessage, setAlertMessage] = useState("");
	const [isLoading, setIsLoading] = useState(false);

	// INPUT HANDLERS
	const firstNameChanged = (event) => {
		setFirstName(event.target.value);
	};

	const lastNameChanged = (event) => {
		setLastName(event.target.value);
	};

	const initialsChanged = (event) => {
		setInitials(event.target.value);
	};

	const oicIDChanged = (event) => {
		setOicID(event.target.value);
	};

	const emailChanged = (event) => {
		setEmail(event.target.value);
	};

	const cellPhoneChanged = (event) => {
		setCellPhone(formatPhoneNumber(event.target.value));
	};

	const deskPhoneChanged = (event) => {
		setDeskPhone(formatPhoneNumber(event.target.value));
	};

	const hubspotIDChanged = (event) => {
		setHubspotID(event.target.value);
	};

	const companyCamIDChanged = (event) => {
		setCompanyCamID(event.target.value);
	};

	const ripplingIDChanged = (event) => {
		setRipplingID(event.target.value);
	};

	const bqeIDChanged = (event) => {
		setBqeID(event.target.value);
	};

	const teamsIDChanged = (event) => {
		setTeamsID(event.target.value);
	};

	const jobTitleChanged = (event) => {
		setJobTitle(event);
	};

	const departmentChanged = (event) => {
		setDepartment(event);
	};

	const isActiveChanged = (event) => {
		setIsActive(event.target.value);
	};

	// SUBMIT HANDLER
	const submitButtonClicked = async () => {
		if (!validateInputFields()) return;

		setIsLoading(true);

		let res = await postAPICall(
			instance,
			accounts[0],
			"/api/employees/add",
			{
				firstName: firstName,
				lastName: lastName,
				initials: initials,
				oicID: oicID !== 0 ? oicID : null,
				cellPhone: cellPhone,
				deskPhone: deskPhone,
				email: email,
				hubspotID: hubspotID ? hubspotID : null,
				companyCamID: companyCamID ? companyCamID : null,
				ripplingID: ripplingID ? ripplingID : null,
				bqeID: bqeID ? bqeID : null,
				teamsID: teamsID ? teamsID : null,
				jobTitleID: jobTitle ? getJobTitleIdFromName(jobTitle) : null,
				departmentID: department ? getDepartmentIdFromName(department) : null,
				isActive: isActive,
				employmentType: employmentType || null,
				hoursPerWeek: hoursPerWeek !== "" ? hoursPerWeek : null,
				modifiedBy: employeeID,
			}
		);

		setIsLoading(false);

		formClosed();
		props.fetchEmployees();
	};

	// API FUNCTIONS

	// HELPER FUNCTIONS
	const resetForm = () => {
		setFirstName("");
		setLastName("");
		setInitials("");

		setOicID(0);
		setCellPhone("");
		setDeskPhone("");
		setEmail("");

		setHubspotID("");
		setCompanyCamID("");
		setRipplingID("");
		setBqeID("");
		setTeamsID("");
		setJobTitle("");
		setDepartment("");
		setIsActive(1);
		setEmploymentType("");
		setHoursPerWeek("");

		setAlertOpen(false);
		setAlertMessage("");
		setIsLoading(false);
	};

	const validateInputFields = () => {
		if (
			!firstName ||
			!lastName ||
			!initials ||
			!email ||
			!verifyEmailRegex(email)
		) {
			setAlertMessage("Please fill out all fields (first name, last name, email, initials).");
			setAlertOpen(true);
			return false;
		}

		if (hoursPerWeek !== "" && Number(hoursPerWeek) > 168) {
			setAlertMessage("Hours per week cannot exceed 168 (24 × 7).");
			setAlertOpen(true);
			return false;
		}

		return true;
	};

	const formClosed = () => {
		resetForm();
		props.newEmployeeDialogClosed();
	};

	const getJobTitleIdFromName = (jobTitleName) => {
		let jobTitle = props.jobTitles.find(
			(jobTitle) => jobTitle.name === jobTitleName
		);
		return jobTitle.id;
	};

	const getDepartmentIdFromName = (departmentName) => {
		let department = props.departments.find(
			(department) => department.name === departmentName
		);
		return department ? department.id : null;
	};

	// RENDER
	return (
		<Dialog
			id="newEmployeeDialog"
			open={props.open}
			fullWidth
			maxWidth="lg"
			onClose={formClosed}
		>
			<DialogTitle id="newEmployeeDialogTitle">New Employee</DialogTitle>
			<DialogContent id="newEmployeeDialogContent">
				{isLoading && (
					<div className="loadingSpinnerContainer">
						<CircularProgress color="primary" />
					</div>
				)}
				{!isLoading && (
					<>
						<div className="newEmployeeDialogRow">
							<Collapse
								sx={{ flex: 1 }}
								id="newEmployeeCollapse"
								in={alertOpen}
							>
								<Alert
									id="newEmployeeAlert"
									severity="error"
									action={
										<IconButton
											id="newEmployeeCloseIconButton"
											aria-label="close"
											color="inherit"
											size="small"
											onClick={() => {
												setAlertOpen(false);
											}}
										>
											<CloseIcon
												id="newEmployeeCloseIcon"
												fontSize="inherit"
											/>
										</IconButton>
									}
								>
									{alertMessage}
								</Alert>
							</Collapse>
						</div>
						<div className="newEmployeeDialogRow">
							<TextField
								sx={{ flex: 3 }}
								id="newEmployeeFirstName"
								label="First Name"
								value={firstName}
								onChange={firstNameChanged}
							/>
							<TextField
								sx={{ flex: 3 }}
								id="newEmployeeLastName"
								label="Last Name"
								value={lastName}
								onChange={lastNameChanged}
							/>
							<TextField
								sx={{ flex: 1 }}
								id="newEmployeeInitials"
								label="Initials"
								value={initials}
								onChange={initialsChanged}
							/>
						</div>
						<div className="newEmployeeDialogRow">
							<FormControl sx={{ flex: 1 }}>
								<InputLabel id="newEmployeeOicIDLabel">
									OIC
								</InputLabel>
								<Select
									labelId="newEmployeeOicIDLabel"
									id="newEmployeeOicID"
									value={oicID}
									label="OIC"
									onChange={oicIDChanged}
								>
									{props.offices.map((office, index) => (
										<MenuItem key={index} value={office.id}>
											{office.state}
										</MenuItem>
									))}
								</Select>
							</FormControl>
							<TextField
								sx={{ flex: 3 }}
								id="newEmployeeEmail"
								label="Email"
								value={email}
								onChange={emailChanged}
								error={!verifyEmailRegex(email) && email !== ""}
							/>
							<TextField
								sx={{ flex: 3 }}
								id="newEmployeeCellPhone"
								label="Phone (Cell)"
								value={cellPhone}
								onChange={cellPhoneChanged}
								placeholder="(123) 456-7890"
								inputProps={{ inputMode: "numeric" }}
							/>
							<TextField
								sx={{ flex: 3 }}
								id="newDeskPhone"
								label="Phone (Desk)"
								value={deskPhone}
								onChange={deskPhoneChanged}
								placeholder="(123) 456-7890"
								inputProps={{ inputMode: "numeric" }}
							/>
						</div>
						<div className="newEmployeeDialogRow">
							<TextField
								sx={{ flex: 1 }}
								id="newEmployeeHubspotID"
								label="Hubspot ID"
								value={hubspotID}
								onChange={hubspotIDChanged}
							/>
							<TextField
								sx={{ flex: 1 }}
								id="newEmployeeCompanyCamID"
								label="CompanyCam ID"
								value={companyCamID}
								onChange={companyCamIDChanged}
							/>
							<TextField
								sx={{ flex: 1 }}
								id="newEmployeeRipplingID"
								label="Rippling ID"
								value={ripplingID}
								onChange={ripplingIDChanged}
							/>
							<TextField
								sx={{ flex: 1 }}
								id="newEmployeeBqeID"
								label="BQE ID"
								value={bqeID}
								onChange={bqeIDChanged}
							/>
						</div>
						<div className="newEmployeeDialogRow">
							<TextField
								sx={{ flex: 1 }}
								id="newEmployeeTeamsID"
								label="Teams ID"
								value={teamsID}
								onChange={teamsIDChanged}
							/>
						</div>
						<div className="newEmployeeDialogRow">
							<Autocomplete
								sx={{ flex: 1 }}
								id="newEmployeeJobTitle"
								options={props.jobTitles.map(
									(jobTitle) => jobTitle.name
								)}
								renderInput={(params) => (
									<TextField {...params} label="Job Title" />
								)}
								onChange={(event, value) => {
									jobTitleChanged(value);
								}}
								value={jobTitle}
							/>
							<Autocomplete
								sx={{ flex: 1 }}
								id="newEmployeeDepartment"
								options={props.departments.map(
									(department) => department.name
								)}
								renderInput={(params) => (
									<TextField {...params} label="Department" />
								)}
								onChange={(event, value) => {
									departmentChanged(value);
								}}
								value={department}
							/>
							<FormControl sx={{ flex: 1 }}>
								<InputLabel id="newEmployeeIsActiveLabel">
									Status
								</InputLabel>
								<Select
									labelId="newEmployeeIsActiveLabel"
									id="newEmployeeIsActive"
									value={isActive ? 1 : 0}
									label="Status"
									onChange={isActiveChanged}
								>
									<MenuItem value={1}>Active</MenuItem>
									<MenuItem value={0}>Inactive</MenuItem>
								</Select>
							</FormControl>
						</div>
						<div className="newEmployeeDialogRow">
							<FormControl sx={{ flex: 1 }}>
								<InputLabel id="newEmployeeEmploymentTypeLabel">
									Employment Type
								</InputLabel>
								<Select
									labelId="newEmployeeEmploymentTypeLabel"
									id="newEmployeeEmploymentType"
									value={employmentType}
									label="Employment Type"
									onChange={(e) => setEmploymentType(e.target.value)}
								>
									<MenuItem value=""><em>Not Set</em></MenuItem>
									<MenuItem value="full_time">Full Time</MenuItem>
									<MenuItem value="part_time">Part Time</MenuItem>
								</Select>
							</FormControl>
							<TextField
								sx={{ flex: 1 }}
								id="newEmployeeHoursPerWeek"
								label="Hours Per Week"
								type="number"
								value={hoursPerWeek}
								onChange={(e) => setHoursPerWeek(e.target.value)}
								inputProps={{ min: 0, max: 168, step: 0.5 }}
								helperText="Max 168 hrs/week (24 × 7)"
								error={hoursPerWeek !== "" && Number(hoursPerWeek) > 168}
							/>
						</div>
					</>
				)}
				<div className="dialogButtons">
					<Button
						onClick={formClosed}
						variant="outlined"
						color="error"
					>
						Cancel
					</Button>
					<Button onClick={submitButtonClicked} variant="contained">
						Submit
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default NewEmployeeDialog;
